import { DisplayUnitType } from "./DisplayUnitType";

export class StyleUtil {
    public static numberAsPixels(number: number): string {
        return `${number}px`;
    }

    public static numberAsRems(number: number): string {
        return `${number}rem`;
    }

    public static getRootFontSize(): number {
        return parseFloat(getComputedStyle(document.documentElement).fontSize);
    }

    public static remToPixels(rem: number): number {
        return rem * this.getRootFontSize();
    }

    public static pixelsToRems(pixels: number, fontSize: number = this.getRootFontSize()): number {
        return pixels/fontSize;
    }
    
    /**
     * Converts a rem or pixel value (provided as a number or a string) into either a rem or pixel value (output as
     * either a number or a string);
     *
     * @param value The provided number or string value
     * @param inputType The type of value provided
     * @param returnType The type of value to return
     * @param rootFontSize The font size to use in any conversion involving rems; defaults to the document's font size
     * @returns A number or string containing the result of the value conversion.
     */
    public static convertUnits(value: number|string, inputType: DisplayUnitType, returnType: DisplayUnitType,
        rootFontSize = this.getRootFontSize()): number | string {
        // Normalize input to a number representing pixels
        let pixelValue: number;

        switch (inputType) {
            case DisplayUnitType.PIXELS:
                if (typeof value !== 'number') {
                    throw new Error('Input type is px, but value is not a number.');
                }
                pixelValue = value;
                break;
            case DisplayUnitType.REMS:
                if (typeof value !== 'number') {
                    throw new Error('Input type is rem, but value is not a number.');
                }
                pixelValue = value * rootFontSize;
                break;
            case DisplayUnitType.PIXELS_STRING:
                if (typeof value !== 'string' || !value.endsWith('px')) {
                    throw new Error('Input type is px-string, but value is not a valid px string.');
                }
                pixelValue = parseFloat(value);
                break;
            case DisplayUnitType.REMS_STRING:
                if (typeof value !== 'string' || !value.endsWith('rem')) {
                    throw new Error('Input type is rem-string, but value is not a valid rem string.');
                }
                pixelValue = parseFloat(value) * rootFontSize;
                break;
            default:
                throw new Error(`Invalid input type: ${inputType}`);
        }

        // Convert to the desired output format
        switch (returnType) {
            case DisplayUnitType.PIXELS:
                return pixelValue;
            case DisplayUnitType.PIXELS_STRING:
                return this.numberAsPixels(pixelValue);
            case DisplayUnitType.REMS:
                return this.pixelsToRems(pixelValue, rootFontSize);
            case DisplayUnitType.REMS_STRING:
                return this.numberAsRems(this.pixelsToRems(pixelValue, rootFontSize));
            default:
                throw new Error(`Invalid return type: ${returnType}`);
        }
    }
}
